import { supabase } from './supabaseClient';
import type { OrderStatusBadge, QnAItem } from '@/types/auth';

// Real core_orders.status -> the storefront's simpler badge vocabulary.
// COD is currently the only live payment method, so there's no distinct
// "payment confirmed, not yet shipped" state to show as 'paid' — that
// value stays reserved for when bKash/Nagad go live.
const STATUS_MAP: Record<string, OrderStatusBadge> = {
  placed: 'pending',
  confirmed: 'pending',
  shipped: 'shipped',
  dispatched: 'shipped',
  delivered: 'delivered',
  cod_collected: 'delivered',
  remitted: 'delivered',
  settled: 'delivered',
  cancelled: 'cancelled',
  returned: 'cancelled',
};

export function mapOrderStatus(raw: string): OrderStatusBadge {
  return STATUS_MAP[raw] ?? 'pending';
}

export interface MyOrderItem {
  sku: string;
  name: string;
  qty: number;
  unitPriceBdt: number;
  lineTotalBdt: number;
}

export interface MyOrderShipment {
  courier: string | null;
  trackingNo: string | null;
  dispatchedAt: string | null;
  deliveredAt: string | null;
  codStatus: string | null;
  codAmountBdt: number | null;
}

export interface MyOrder {
  id: string;
  orderNo: string;
  status: OrderStatusBadge;
  rawStatus: string;
  paymentMethod: string | null;
  placedAt: string;
  totalBdt: number;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  items: MyOrderItem[];
  shipment: MyOrderShipment | null;
}

interface RawOrderRow {
  id: string;
  order_no: string;
  status: string;
  payment_method: string | null;
  placed_at: string;
  total_bdt: number;
  customer_info: { name?: string; phone?: string; address?: string } | null;
  core_order_items: { sku: string; qty: number; unit_price_bdt: number | null; line_total_bdt: number | null }[];
  core_shipments: {
    courier: string | null;
    tracking_no: string | null;
    dispatched_at: string | null;
    delivered_at: string | null;
    cod_status: string | null;
    cod_amount_bdt: number | null;
  }[];
}

async function mapRowsToOrders(rows: RawOrderRow[]): Promise<MyOrder[]> {
  const skus = [...new Set(rows.flatMap((r) => r.core_order_items.map((i) => i.sku)))];
  let nameBySku: Record<string, string> = {};
  if (skus.length > 0) {
    const { data: prods } = await supabase.from('core_products').select('sku, name_en').in('sku', skus);
    nameBySku = Object.fromEntries(((prods as { sku: string; name_en: string | null }[]) ?? []).map((p) => [p.sku, p.name_en ?? p.sku]));
  }

  return rows.map((r) => {
    const shipment = r.core_shipments?.[0];
    return {
      id: r.id,
      orderNo: r.order_no,
      status: mapOrderStatus(r.status),
      rawStatus: r.status,
      paymentMethod: r.payment_method,
      placedAt: r.placed_at,
      totalBdt: r.total_bdt,
      customerName: r.customer_info?.name ?? '',
      customerPhone: r.customer_info?.phone ?? '',
      customerAddress: r.customer_info?.address ?? '',
      items: (r.core_order_items ?? []).map((i) => ({
        sku: i.sku,
        name: nameBySku[i.sku] ?? i.sku,
        qty: i.qty,
        unitPriceBdt: i.unit_price_bdt ?? 0,
        lineTotalBdt: i.line_total_bdt ?? 0,
      })),
      shipment: shipment
        ? {
            courier: shipment.courier,
            trackingNo: shipment.tracking_no,
            dispatchedAt: shipment.dispatched_at,
            deliveredAt: shipment.delivered_at,
            codStatus: shipment.cod_status,
            codAmountBdt: shipment.cod_amount_bdt,
          }
        : null,
    };
  });
}

export async function fetchMyOrders(customerId: string): Promise<MyOrder[]> {
  const { data } = await supabase
    .from('core_orders')
    .select('id, order_no, status, payment_method, placed_at, total_bdt, customer_info, core_order_items(sku, qty, unit_price_bdt, line_total_bdt), core_shipments(courier, tracking_no, dispatched_at, delivered_at, cod_status, cod_amount_bdt)')
    .eq('customer_id', customerId)
    .order('placed_at', { ascending: false });
  return mapRowsToOrders((data as RawOrderRow[]) ?? []);
}

export async function fetchMyOrderById(customerId: string, orderId: string): Promise<MyOrder | null> {
  const { data } = await supabase
    .from('core_orders')
    .select('id, order_no, status, payment_method, placed_at, total_bdt, customer_info, core_order_items(sku, qty, unit_price_bdt, line_total_bdt), core_shipments(courier, tracking_no, dispatched_at, delivered_at, cod_status, cod_amount_bdt)')
    .eq('customer_id', customerId)
    .eq('id', orderId)
    .maybeSingle();
  if (!data) return null;
  const mapped = await mapRowsToOrders([data as RawOrderRow]);
  return mapped[0] ?? null;
}

// Same shape as fetchMyOrders, scoped to partner_id instead of customer_id —
// returns every order tied to a reseller regardless of who placed it
// (staff via New Order, or the reseller themselves via self-checkout).
export async function fetchResellerOrders(partnerId: string): Promise<MyOrder[]> {
  const { data } = await supabase
    .from('core_orders')
    .select('id, order_no, status, payment_method, placed_at, total_bdt, customer_info, core_order_items(sku, qty, unit_price_bdt, line_total_bdt), core_shipments(courier, tracking_no, dispatched_at, delivered_at, cod_status, cod_amount_bdt)')
    .eq('partner_id', partnerId)
    .order('placed_at', { ascending: false });
  return mapRowsToOrders((data as RawOrderRow[]) ?? []);
}

interface RawQnARow {
  id: string;
  category: string;
  question: string;
  status: string;
  answer: string | null;
  answered_at: string | null;
  created_at: string;
}

export async function fetchMyQnA(customerId: string): Promise<QnAItem[]> {
  const { data } = await supabase
    .from('rxk_qna')
    .select('id, category, question, status, answer, answered_at, created_at')
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false });
  return ((data as RawQnARow[]) ?? []).map((r) => ({
    id: r.id,
    title: r.question,
    channel: r.category,
    status: r.status as QnAItem['status'],
    createdAt: r.created_at.slice(0, 10),
    answer: r.answer ?? undefined,
    answeredAt: r.answered_at?.slice(0, 10),
  }));
}

export async function submitQuestion(customerId: string, category: string, question: string, productSku?: string): Promise<void> {
  const { error } = await supabase.from('rxk_qna').insert({ customer_id: customerId, category, question, product_sku: productSku ?? null });
  if (error) throw error;
}

export interface ShippingAddressInput {
  address1: string;
  address2: string;
  city: string;
  area: string;
  postalCode: string;
}

export async function saveShippingAddress(customerId: string, address: ShippingAddressInput): Promise<void> {
  const { error } = await supabase
    .from('rxk_customers')
    .update({
      address_line1: address.address1 || null,
      address_line2: address.address2 || null,
      city: address.city || null,
      area: address.area || null,
      postal_code: address.postalCode || null,
    })
    .eq('id', customerId);
  if (error) throw error;
}
